import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { step0Schema, loginSchema } from '@shared/lib/validators/registration-schema';
import { useForm } from 'react-hook-form';
import {
  selectCredentials,
  selectCurrentStep,
  setExistingUser,
  setStep,
} from '../../../entities/registration/model';
import { useEffect, useState } from 'react';
import { Button } from '../../../shared/ui/button';
import { InputField } from '../../../shared/ui/input-field';
import { useParams } from 'react-router-dom';
import { RegisterHeader } from './RegistrationStepCompanyData';
import { CustomModal } from '../../../shared/ui/custom-modal';
import {
  fetchInviteById,
  selectCurrentInvite,
  selectInviteStatus,
} from '../../../entities/invite/model';
import {
  checkUsernameAvailable,
  registerEmployee,
} from '../../../entities/registration/model';
import ReactInputMask from 'react-input-mask';
import { useAppDispatch, useAppSelector } from '../../../app/store/store';
import { authApi } from '../../../api/services/auth.service';
import {
  setErrorNotification,
  setNotification,
} from '../../../app/store/notifications';

export const RegistrationStepCredentials = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectInviteStatus);
  const step = useAppSelector(selectCurrentStep);
  const credentialData = useAppSelector(selectCredentials);
  const invite = useAppSelector(selectCurrentInvite);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(step0Schema),
    shouldUnregister: false,
    defaultValues: {
      email: '',
      username: '',
      password: '',
      terms: false,
      first_name: '',
      last_name: '',
      mobile_phone: '',
    },
  });

  const {
    register: loginRegister,
    handleSubmit: loginHandleSubmit,
    formState: { errors: loginErrors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  useEffect(() => {
    reset(credentialData);
  }, [credentialData, reset]);

  useEffect(() => {
    // ВАЖНО: стреляем ТОЛЬКО при `idle` — то есть один раз за
    // монтирование. Раньше тут было `status !== 'succeeded'`, и
    // любой не-успешный ответ (loading/failed) триггерил effect
    // снова: при 429 от throttle status переходил failed → effect
    // снова дёргал fetch → снова 429 → бесконечный цикл (см. сетевой
    // лог 18.05.2026, всё сотками 429). С `idle` цикла нет: если
    // первый запрос упал, retry — это уже явное действие юзера
    // (reload страницы), а не невидимый авто-loop.
    if (status === 'idle') {
      dispatch(fetchInviteById(id?.toString() || ''));
    }
  }, [dispatch, status, id]);

  const onSubmit = async (data: Record<string, unknown>) => {
    // ВАЖНО: yup-схема (step0Schema) не знает про «логин занят» —
    // это решает бэк. Если опираться только на setError из onBlur, при
    // нажатии «Продолжить» валидация yup срабатывает первой и затирает
    // ручную ошибку — красная подсветка слетала и юзеру казалось, что
    // форма «как будто принята». Поэтому проверяем доступность логина
    // прямо здесь, ПОСЛЕ успешной yup-валидации. setError в onSubmit
    // становится «последним словом» и держит подсветку до тех пор,
    // пока юзер не изменит поле.
    //
    // Локальные тосты «Логин уже занят» убраны намеренно: дальше в
    // ветке для invite.type !== 'employee' дёргается thunk
    // `checkUsernameAvailable` (см. entities/registration/model), у
    // которого свой собственный setErrorNotification. Из-за этого
    // юзер видел два одинаковых тоста подряд. Здесь только подсветка
    // и фокус, тост (если дойдёт до thunk) скажет сам бэк.
    const username = String(data.username || '').trim();
    if (username) {
      try {
        const response = await authApi.checkUsernameAvailable(username);
        if (!response.data) {
          setError(
            'username',
            {
              type: 'manual',
              message: 'Логин уже занят. Выберите другой.',
            },
            { shouldFocus: true }
          );
          return;
        }
      } catch (error) {
        dispatch(setErrorNotification('Не удалось проверить логин.'));
        return;
      }
    }

    if (invite.type === 'employee') {
      dispatch(registerEmployee({ ...data, invite: invite.id }));
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(checkUsernameAvailable({ ...data, ...invite } as any));
  };

  const handleUsernameBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    // Мгновенная обратная связь: ставим только подсветку поля, без
    // тоста — иначе юзер при сабмите видел бы дубль (одно уведомление
    // от blur, другое от onSubmit/thunk). «Громкий» тост оставлен на
    // долю серверной ошибки.
    const username = event.target.value;
    if (username) {
      authApi.checkUsernameAvailable(username).then((response) => {
        if (response.data) {
          dispatch(setNotification('Логин не занят!'));
        } else if (!response.data) {
          setError('username', {
            type: 'manual',
            message: 'Логин занят!',
          });
        }
      });
    }
  };

  const onLoginSubmit = async (data: any) => {
    setIsLoggingIn(true);
    setLoginError('');
    try {
      const response = await authApi.login(data.username, data.password);
      const token = response.data.token;
      const user = response.data.user;

      // (Гостевые инвайты выпилены — соответствующая ветка use_guest_invite
      // удалена. Все приглашения ведут через стандартный регистрационный
      // флоу или employee-инвайт.)

      dispatch(
        setExistingUser({
          token,
          user: {
            username: user.username,
            email: user.email,
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            mobile_phone: user.mobile_phone || '',
            password: '',
            terms: true,
          },
        })
      );
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail || 'Ошибка входа. Проверьте логин и пароль.';
      setLoginError(errorMessage);
      dispatch(setErrorNotification(errorMessage));
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (showLoginForm) {
    return (
      <>
        <RegisterHeader step={step} title="Вход в существующий аккаунт" />
        <div style={{ marginBottom: '15px', textAlign: 'center' }}>
          <span style={{ color: '#626262', fontSize: '16px' }}>Нет аккаунта? </span>
          <span
            onClick={() => { setShowLoginForm(false); setLoginError(''); }}
            style={{ cursor: 'pointer', color: '#E3282A', fontWeight: 600 }}
          >
            Зарегистрироваться
          </span>
        </div>
        <form className="w-full" onSubmit={loginHandleSubmit(onLoginSubmit)}>
          <InputField inputName="username" id="login-username" label="Email или Логин" placeholder="Введите Email или Логин" register={loginRegister} errors={loginErrors} />
          <InputField inputName="password" type="password" id="login-password" label="Пароль" placeholder="Введите пароль" register={loginRegister} errors={loginErrors} />
          {loginError && (
            <p style={{ color: '#E3282A', fontSize: '14px', marginBottom: '10px' }}>{loginError}</p>
          )}
          <Button type="submit" status={isLoggingIn ? 'loading' : 'idle'}>Войти</Button>
        </form>
      </>
    );
  }

  return (
    <>
      <RegisterHeader step={step} title="Регистрационные данные" />
      <div style={{ marginBottom: '15px', textAlign: 'center' }}>
        <span style={{ color: '#626262', fontSize: '16px' }}>У вас уже есть аккаунт? </span>
        <span
          onClick={() => setShowLoginForm(true)}
          style={{ cursor: 'pointer', color: '#E3282A', fontWeight: 600 }}
        >
          Войти
        </span>
      </div>
      <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
        <InputField inputName="first_name" id="first_name" label="Имя" register={register} errors={errors} />
        <InputField inputName="last_name" id="last_name" label="Фамилия" register={register} errors={errors} />
        <InputField inputName="username" id="username" label="Придумайте логин" register={register} errors={errors} onBlur={handleUsernameBlur} />
        <InputField inputName="email" id="email" label="Email" register={register} errors={errors} />
        <InputField inputName="password" type="password" id="password" label="Придумайте пароль" register={register} errors={errors} />
        <InputField
          as={ReactInputMask}
          mask={'+7 (9**) ***-**-**'}
          id="mobile_phone"
          inputName="mobile_phone"
          label="Телефон"
          errors={errors}
          register={register}
        />
        {errors.terms && (
          <span className="text-right text-[#e3282a] text-base font-medium leading-5 mb-2 block">
            {errors.terms.message}
          </span>
        )}
        <div className="flex my-5 items-center">
          <input
            className="bg-[#9e9e9e] accent-[#e3282a] h-5 w-5 align-sub cursor-pointer"
            type="checkbox"
            id="terms"
            {...register('terms')}
          />
          <p className="ml-[10px]">
            Согласен с{' '}
            <CustomModal
              size="xxl"
              openButton={
                <span style={{ cursor: 'pointer', color: 'blue' }}>
                  политикой обработки персональных данных *
                </span>
              }
            >
              <div className="w-[90vw] h-[90vh] overflow-y-scroll">
                <div className="font-bold text-2xl leading-8 text-center text-[#2d2c2c] mt-[75px]">
                  Согласие на обработку персональных данных
                </div>
                <div className="w-[90%] mx-auto mt-[70px]">
                  <div className="font-medium text-sm leading-[150%] text-[#343434]">
                    Пользователь, оставляя заявку (под заявкой понимается любая
                    форма на интернет- сайте, поддерживающая ввод персональных
                    данных пользователя) на интернет-сайте www.lk.fmeter.ru,
                    принимает настоящее Согласие на обработку персональных
                    данных (далее – Согласие). Действуя свободно, своей волей и
                    в своем интересе, а также подтверждая свою дееспособность,
                    Пользователь дает свое согласие ООО «Ойл» (ИНН 1655164731),
                    которое расположено по адресу 420000, Татарстан Республика,
                    ул. Дементьева, д. 2Б, на обработку своих персональных
                    данных со следующими условиями:
                    <br /><br />
                    <span className="font-semibold text-sm leading-[150%] text-[#343434]">1.</span>{' '}
                    Данное Согласие дается на обработку персональных данных, как без использования средств автоматизации, так и с их использованием.
                    <br /><br />
                    <span className="font-semibold text-sm leading-[150%] text-[#343434]">2.</span>{' '}
                    Согласие дается на обработку следующих моих персональных данных: Персональные данные, не являющиеся специальными или биометрическими: номера контактных телефонов; адреса электронной почты; фамилия, имя и отчество пользователя; адрес доставки оборудования; пользовательские данные (сведения о местоположении; тип и версия ОС; тип и версия Браузера; тип устройства и разрешение его экрана; источник, откуда пришел на сайт пользователь; с какого сайта или по какой рекламе; язык ОС и Браузера; какие страницы открывает и на какие кнопки нажимает пользователь; ip-адрес.
                    <br /><br />
                    <span className="font-semibold text-sm leading-[150%] text-[#343434]">3.</span>{' '}
                    Персональные данные не является общедоступными.
                    <br /><br />
                    <span className="font-semibold text-sm leading-[150%] text-[#343434]">4.</span> Цель обработки персональных данных: исполнение договора, а именно: обработка входящих запросов физических лиц с целью оказания консультирования и/или продажи оборудования.
                    <br /><br />
                    <span className="font-semibold text-sm leading-[150%] text-[#343434]">5.</span>{' '}
                    Основанием для обработки персональных данных является: ст. 24 Конституции Российской Федерации; ст. 6 Федерального закона №152 – ФЗ «О персональных данных; Устав ООО «Ойл»; настоящее согласие на обработку персональных данных.
                    <br /><br />
                    <span className="font-semibold text-sm leading-[150%] text-[#343434]">6.</span> В ходе обработки с персональными данными будут совершены следующие действия: сбор; запись; систематизация; накопление; хранение; уточнение (обновление, изменение), извлечение; использование; передача (распространение, предоставление, доступ); блокирование; удаление; уничтожение.
                    <br /><br />
                    <span className="font-semibold text-sm leading-[150%] text-[#343434]">7.</span>{' '}
                    Обработка персональных данных может быть прекращена по запросу субъекта персональных данных. Хранение персональных данных, зафиксированных на бумажных носителях осуществляется согласно Федеральному закону №125 – ФЗ Об архивном деле в Российской Федерации и иным нормативно правовым актам в области архивного дела и архивного хранения.
                    <br /><br />
                    <span className="font-semibold text-sm leading-[150%] text-[#343434]">8.</span>{' '}
                    Согласие может быть отозвано субъектом персональных данных или его представителем путём направления письменного заявления ООО «Ойл» или его представителю по адресу, указанному в начале данного Согласия.
                    <br /><br />
                    <span className="font-semibold text-sm leading-[150%] text-[#343434]">9.</span> В случае отзыва субъектом персональных данных или его представителем согласия на обработку персональных данных ООО «Ойл» вправе продолжить обработку персональных данных без согласия субъекта персональных данных при наличии оснований, указанных в пунктах 2 – 11 части 1 статьи 6, части 2 статьи 10 и части 2 статьи 11 Федерального закона №152 – ФЗ О персональных данных от 27. 07. 2006 г.
                    <br /><br />
                    <span className="font-semibold text-sm leading-[150%] text-[#343434]">10.</span>{' '}
                    Настоящее Согласие действует все время до момента прекращения обработки персональных данных, указанных в п.7 и п. 8 данного Согласия.
                  </div>
                </div>
              </div>
            </CustomModal>
          </p>
        </div>
        <Button type="submit" status={status}>Продолжить</Button>
      </form>
    </>
  );
};
